import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { resolveLogoUrl } from '@/lib/resolve-logo'
import { useCompanySettings } from '@/hooks/useCompanySettings'
import { useNavigate, useLocation } from 'react-router-dom'
import { hasModulo, STATUS_OBRA, podeEditar, normalizePerfil } from '@/lib/helpers'
import { CLIENTE_MENU_KEYS, isClientePerfil, getPerfilLabel } from '@/lib/perfis'
import { usePerfilPermissions } from '@/hooks/usePerfilPermissions'
import { useState, useMemo, useEffect } from 'react'
import type { NotificationCounts } from '@/components/AppLayout'
import CompanyDropdown from '@/components/CompanyDropdown'
import {
  LayoutDashboard, Home, Building2, FileText, Camera, TrendingUp,
  CheckSquare, AlertCircle, AlertTriangle, Map, CalendarDays,
  Shield, Clock, LogOut, ChevronDown, Menu, X,
  Package, Layers, Warehouse, KeyRound, Users, Stamp, Settings,
  Crown, Wrench, Eye, ClipboardList, ListChecks, MessageSquare, Bot, Bug,
} from 'lucide-react'

const ROLE_LABELS: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  engineer: 'Engenheiro',
  viewer: 'Visualizador',
}

const ROLE_ICONS: Record<string, typeof Crown> = {
  owner: Crown,
  admin: Shield,
  engineer: Wrench,
  viewer: Eye,
}

const NAV_SECTIONS = [
  {
    label: 'Principal',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/' },
      { key: 'obra-dash', label: 'Dashboard da Obra', icon: Home, path: '/obra' },
      { key: 'obras', label: 'Obras', icon: Building2, path: '/obras' },
    ],
  },
  {
    label: 'Operacional',
    items: [
      { key: 'diario', label: 'Diário / RDO', icon: FileText, path: '/diario', badge: 'rdo' },
      { key: 'fotos', label: 'Fotos & Mídia', icon: Camera, path: '/fotos' },
      { key: 'producao', label: 'Produção', icon: TrendingUp, path: '/producao' },
      { key: 'estoque', label: 'Estoque', icon: Warehouse, path: '/estoque', badge: 'estoque' },
    ],
  },
  {
    label: 'Qualidade',
    items: [
      { key: 'fvs', label: 'FVS / Qualidade', icon: CheckSquare, path: '/fvs' },
      { key: 'fvm', label: 'FVM / Materiais', icon: Package, path: '/fvm' },
      { key: 'pendencias', label: 'Pendências', icon: AlertCircle, path: '/pendencias', badge: 'pend' },
      { key: 'ocorrencias', label: 'Ocorrências', icon: AlertTriangle, path: '/ocorrencias', badge: 'ocorr' },
    ],
  },
  {
    label: 'Rastreabilidade',
    items: [
      { key: 'concreto', label: 'Concreto', icon: Layers, path: '/concreto' },
      { key: 'revestimentos', label: 'Revestimentos', icon: Layers, path: '/revestimentos' },
      { key: 'entregas', label: 'Entrega Unidades', icon: KeyRound, path: '/entregas' },
    ],
  },
  {
    label: 'Planejamento',
    items: [
      { key: 'planta', label: 'Planta da Obra', icon: Map, path: '/planta' },
      { key: 'documentos', label: 'Documentos Técnicos', icon: ClipboardList, path: '/documentos' },
      { key: 'cronograma', label: 'Cronograma', icon: CalendarDays, path: '/cronograma' },
      { key: 'mapa-visual', label: 'Mapa Visual', icon: LayoutDashboard, path: '/mapa-visual' },
      { key: 'sst', label: 'Segurança SST', icon: Shield, path: '/sst', badge: 'sst' },
      { key: 'chat', label: 'Chat', icon: MessageSquare, path: '/chat', badge: 'chat' },
      { key: 'relatorios', label: 'Relatórios', icon: FileText, path: '/relatorios' },
      { key: 'relatorio-mensal', label: 'Relatório Mensal', icon: ClipboardList, path: '/relatorio-mensal' },
      { key: 'assistente-ia', label: 'Assistente IA', icon: Bot, path: '/assistente-ia' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { key: 'historico', label: 'Histórico', icon: Clock, path: '/historico' },
      { key: 'identidade', label: 'Identidade', icon: Stamp, path: '/identidade' },
      { key: 'equipe', label: 'Equipe', icon: Users, path: '/equipe' },
      { key: 'admin-users', label: 'Usuários', icon: Users, path: '/admin/usuarios', admin: true },
      { key: 'settings', label: 'Configurações', icon: Settings, path: '/settings' },
      { key: 'superadmin', label: 'Super Admin', icon: Shield, path: '/superadmin', superadmin: true },
      { key: 'system-logs', label: 'Logs do Sistema', icon: Bug, path: '/admin/system-logs', superadmin: true },
    ],
  },
]

interface AppSidebarProps {
  pendCount: number
  ocorrCount: number
  notifs: NotificationCounts
}

export default function AppSidebar({ pendCount, ocorrCount, notifs }: AppSidebarProps) {
  const { user, obras, obraAtiva, setObraAtiva, logout, company, companyLimits } = useAuth()
  const { podeVer: permPodeVer, loading: permLoading, userPermissions: permUserPerms } = usePerfilPermissions()
  const { settings: companySettings } = useCompanySettings()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [obraPickerOpen, setObraPickerOpen] = useState(false)
  const [obraFilter, setObraFilter] = useState('')
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [chatUnread, setChatUnread] = useState(0)
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string | null>(null)
  const [logoError, setLogoError] = useState(false)

  // Resolve logo URL — prefer company_settings logo, fallback to company.logo_url
  useEffect(() => {
    let cancelled = false
    setLogoError(false)
    setResolvedLogoUrl(null)
    const settingsLogo = companySettings.logo_url
    const companyLogo = company?.logo_url

    async function resolve() {
      // 1) Try company_settings logo via signed URL on company-logos bucket
      if (settingsLogo) {
        // If full URL already, just use it
        if (settingsLogo.startsWith('http')) {
          if (!cancelled) setResolvedLogoUrl(settingsLogo)
          return
        }
        // Try signed URL (works regardless of bucket public/private)
        const { data: signed } = await supabase.storage
          .from('company-logos')
          .createSignedUrl(settingsLogo, 3600)
        if (signed?.signedUrl) {
          if (!cancelled) setResolvedLogoUrl(signed.signedUrl)
          return
        }
        // Fallback to public URL
        const { data: pub } = supabase.storage.from('company-logos').getPublicUrl(settingsLogo)
        if (pub?.publicUrl) {
          if (!cancelled) setResolvedLogoUrl(pub.publicUrl)
          return
        }
      }

      // 2) Fallback to company.logo_url (external companies table → fotos-obras bucket)
      if (companyLogo) {
        const url = await resolveLogoUrl(companyLogo)
        if (!cancelled) {
          if (url) setResolvedLogoUrl(url)
          else setLogoError(true)
        }
        return
      }

      // 3) No logo — let fallback render
      if (!cancelled) setLogoError(true)
    }

    resolve()
    return () => { cancelled = true }
  }, [companySettings.logo_url, company?.logo_url])

  // Check platform admin status once
  useEffect(() => {
    supabase.rpc('is_platform_admin').then(({ data }) => {
      if (data === true) setIsPlatformAdmin(true)
    })
  }, [])

  // Check unread chat messages
  useEffect(() => {
    if (!obraAtiva) return
    const lastSeen = localStorage.getItem(`chat_seen_${obraAtiva.id}`)
    if (!lastSeen) {
      // Never visited chat — show badge with recent count
      supabase.from('mensagens').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id)
        .then(({ count }) => setChatUnread(Math.min(count || 0, 99)))
      return
    }
    supabase.from('mensagens').select('id', { count: 'exact', head: true })
      .eq('obra_id', obraAtiva.id)
      .gt('created_at', lastSeen)
      .then(({ count }) => setChatUnread(count || 0))
  }, [obraAtiva, location.pathname])

  const obrasFiltradas = useMemo(() => {
    if (!obraFilter.trim()) return obras
    const q = obraFilter.toLowerCase()
    return obras.filter(o => o.nome.toLowerCase().includes(q) || (o.cliente_nome || '').toLowerCase().includes(q))
  }, [obras, obraFilter])

  const initials = (user?.nome || '').split(' ').map(n => n[0]).join('').slice(0, 2) || '?'
  const isCliente = isClientePerfil(user?.perfil)
  const RoleIcon = ROLE_ICONS[user?.company_role || 'viewer'] || Eye
  // Para clientes, mostrar "Cliente" em vez do company_role
  const roleLabel = isCliente
    ? 'Cliente'
    : (ROLE_LABELS[user?.company_role || 'viewer'] || getPerfilLabel(user?.perfil) || 'Visualizador')

  const handleNav = (path: string) => {
    navigate(path)
    setMobileOpen(false)
  }

  const handleObraSelect = (obra: typeof obraAtiva) => {
    if (obra) {
      setObraAtiva(obra)
      setObraPickerOpen(false)
    }
  }

  const getBadgeCount = (badge?: string) => {
    if (badge === 'pend') return pendCount
    if (badge === 'ocorr') return ocorrCount
    if (badge === 'rdo' && notifs.rdosPendentes) return 1
    if (badge === 'sst' && notifs.sstProximas > 0) return notifs.sstProximas
    if (badge === 'chat') return chatUnread
    return 0
  }

  const getBadgeStyle = (badge?: string) => {
    if (badge === 'rdo') return 'pill-amber'
    if (badge === 'sst') return 'pill-sky'
    if (badge === 'chat') return 'pill-sky'
    if (badge === 'ocorr') return 'pill-rose'
    return 'pill-rose'
  }

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-3 left-3 z-[501] md:hidden w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
        style={{ background: 'hsl(var(--bg3))', border: '1px solid hsl(var(--line2))' }}
      >
        <Menu size={16} className="text-foreground" />
      </button>

      {mobileOpen && (
        <div className="fixed inset-0 z-[499] bg-black/50 backdrop-blur-sm md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`
          fixed md:sticky top-0 left-0 bottom-0 z-[500] w-[252px] flex-shrink-0 flex flex-col overflow-hidden transition-transform duration-200
          md:translate-x-0
          ${mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
        `}
        style={{
          background: 'hsl(var(--bg1))',
          borderRight: '1px solid hsl(var(--line))',
        }}
      >
        {/* Header — Company logo + info */}
        <div className="px-3 py-3 flex-shrink-0" style={{ borderBottom: '1px solid hsl(var(--line))' }}>
          {/* Logo */}
          <div
            className="cursor-pointer flex flex-col items-center gap-1.5 mb-2"
            onClick={() => { handleNav('/') }}
          >
            {resolvedLogoUrl && !logoError ? (
              <img
                src={resolvedLogoUrl}
                alt={company?.name || 'Logo'}
                className="object-contain"
                style={{ maxWidth: 120, maxHeight: 48, background: 'transparent', mixBlendMode: 'normal' }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-black text-primary-foreground flex-shrink-0"
                style={{ background: 'hsl(var(--amber))' }}
              >
                {(company?.name || 'E').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>

          {/* Company name + user info */}
          <div className="text-center">
            <div className="text-[11px] font-bold text-foreground truncate">
              {company?.name || 'Construzilla'}
            </div>
            <div className="text-[10px] font-semibold text-muted-foreground truncate mt-0.5 flex items-center justify-center gap-1" style={{ opacity: 1 }}>
              <span>{(user?.nome || '').split(' ').slice(0, 2).join(' ')}</span>
              <span>·</span>
              <span className="font-semibold">{roleLabel}</span>
            </div>
          </div>

          {/* Mobile close */}
          <button onClick={() => setMobileOpen(false)} className="absolute top-3 right-3 md:hidden w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" style={{ border: '1px solid hsl(var(--line2))' }}>
            <X size={14} />
          </button>
        </div>

        {/* Company context dropdown */}
        <CompanyDropdown />

        {/* Obra chip */}
        <div
          className="mx-2 mt-2 rounded-xl p-2.5 cursor-pointer transition-all flex-shrink-0 group"
          style={{
            background: 'hsl(var(--bg3))',
            border: '1px solid hsl(var(--line2))',
          }}
          onClick={() => setObraPickerOpen(!obraPickerOpen)}
        >
          <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1" style={{ opacity: 1 }}>Obra ativa</div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-foreground truncate flex-1">
              {obraAtiva?.nome || 'Selecione uma obra'}
            </span>
            <ChevronDown size={11} className="text-muted-foreground flex-shrink-0 group-hover:text-foreground transition-colors" />
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="flex-1 h-[3px] rounded-full overflow-hidden" style={{ background: 'hsl(var(--line2))' }}>
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${obraAtiva?.avanco_fisico || 0}%` }} />
            </div>
            <span className="text-[10px] font-bold text-primary min-w-[28px] text-right">
              {obraAtiva?.avanco_fisico || 0}%
            </span>
          </div>
        </div>

        {/* Obra picker dropdown */}
        {obraPickerOpen && (
          <div className="mx-2 mt-1 rounded-xl overflow-hidden max-h-[240px] overflow-y-auto flex-shrink-0" style={{ background: 'hsl(var(--bg2))', border: '1px solid hsl(var(--line2))' }}>
            <div className="p-2 sticky top-0" style={{ background: 'hsl(var(--bg2))' }}>
              <input
                type="text"
                placeholder="Filtrar obra..."
                value={obraFilter}
                onChange={e => setObraFilter(e.target.value)}
                className="w-full h-8 px-2.5 rounded-lg text-xs bg-transparent text-foreground placeholder:text-muted-foreground"
                style={{ border: '1px solid hsl(var(--line2))' }}
              />
            </div>
            {obrasFiltradas.map(o => (
              <div
                key={o.id}
                className={`px-3 py-2.5 cursor-pointer transition-colors ${obraAtiva?.id === o.id ? 'border-l-[3px] border-l-primary' : ''}`}
                style={{
                  background: obraAtiva?.id === o.id ? 'hsl(var(--amber-bg))' : 'transparent',
                  borderBottom: '1px solid hsl(var(--line))',
                }}
                onClick={() => handleObraSelect(o)}
              >
                <div className="text-[13px] font-semibold text-foreground truncate">{o.nome}</div>
                <div className="text-[10px] font-medium text-muted-foreground truncate mt-0.5" style={{ opacity: 1 }}>
                  {o.cliente_nome || '—'} · {STATUS_OBRA[o.status] || o.status}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <div className="flex-1 h-[2px] rounded-full overflow-hidden" style={{ background: 'hsl(var(--line2))' }}>
                    <div className="h-full bg-primary rounded-full" style={{ width: `${o.avanco_fisico || 0}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-primary">{o.avanco_fisico || 0}%</span>
                </div>
              </div>
            ))}
            {obrasFiltradas.length === 0 && (
              <div className="p-4 text-center text-xs font-medium text-muted-foreground" style={{ opacity: 1 }}>Nenhuma obra encontrada</div>
            )}
            {podeEditar(user?.perfil) && (
              <button
                onClick={() => { setObraPickerOpen(false); navigate('/obras') }}
                className="w-full px-3 py-2.5 text-xs font-bold text-center transition-colors hover:brightness-110"
                style={{ background: 'hsl(var(--bg3))', color: 'hsl(var(--amber))' }}
              >
                + Adicionar obra
              </button>
            )}
          </div>
        )}

        {/* Notification alerts */}
        {(notifs.pendVencidas > 0 || notifs.ocorrCriticas > 0 || notifs.rdosPendentes) && (
          <div className="mx-2 mt-2 rounded-xl p-2 space-y-1 flex-shrink-0" style={{ background: 'hsl(var(--rose-bg))', border: '1px solid hsl(var(--rose) / 0.2)' }}>
            {notifs.pendVencidas > 0 && (
              <div className="text-[10px] font-semibold cursor-pointer" style={{ color: 'hsl(var(--rose))' }} onClick={() => handleNav('/pendencias')}>
                ⏰ {notifs.pendVencidas} pendência{notifs.pendVencidas > 1 ? 's' : ''} vencida{notifs.pendVencidas > 1 ? 's' : ''}
              </div>
            )}
            {notifs.ocorrCriticas > 0 && (
              <div className="text-[10px] font-semibold cursor-pointer" style={{ color: 'hsl(var(--rose))' }} onClick={() => handleNav('/ocorrencias')}>
                ⚠️ {notifs.ocorrCriticas} ocorrência{notifs.ocorrCriticas > 1 ? 's' : ''} crítica{notifs.ocorrCriticas > 1 ? 's' : ''}
              </div>
            )}
            {notifs.rdosPendentes && (
              <div className="text-[10px] font-semibold cursor-pointer" style={{ color: 'hsl(var(--amber))' }} onClick={() => handleNav('/diario')}>
                📋 RDO não preenchido hoje
              </div>
            )}
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-1.5 py-1 min-h-0">
          {NAV_SECTIONS.map(section => {
            const visibleItems = section.items.filter(item => {
              // Cliente: somente itens permitidos no menu simplificado
              if (isCliente) return CLIENTE_MENU_KEYS.has(item.key)
              // Super admin page: platform admins only
              if ((item as any).superadmin) return isPlatformAdmin
              // Admin page: dono/gerente only
              if ((item as any).admin) {
                const np = normalizePerfil(user?.perfil || '')
                return np === 'dono' || np === 'gerente'
              }
              // Module access check (modulos_ativos)
              if (!hasModulo(item.key, user?.perfil, user?.modulos_ativos)) return false
              return true
            })
            if (visibleItems.length === 0) return null
            return (
            <div key={section.label}>
              <div className="text-[9px] font-bold text-foreground uppercase tracking-[0.12em] px-2 pt-3 pb-1" style={{ opacity: 0.95 }}>
                {section.label}
              </div>
              {visibleItems.map(item => {
                const active = location.pathname === item.path
                // Use centralized DB permissions for lock state
                // Bypass lock: while loading, for privileged roles, for cliente (já filtrado), or when permissions data is empty (query failed / not configured)
                const isPrivileged = user?.company_role === 'owner' || user?.company_role === 'admin'
                const permDataEmpty = !permLoading && permUserPerms.size === 0
                const locked = (item as any).superadmin || isCliente ? false : (permLoading || isPrivileged || permDataEmpty) ? false : !permPodeVer(item.key)
                const Icon = item.icon
                const badgeCount = getBadgeCount((item as any).badge)

                return (
                  <button
                    key={item.key}
                    onClick={() => !locked && handleNav(item.path)}
                    disabled={locked}
                    className={`
                      w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12.5px] font-medium transition-all mb-0.5 relative whitespace-nowrap text-left
                      ${active ? 'font-semibold' : 'text-foreground hover:bg-secondary font-medium'}
                      ${locked ? 'opacity-90 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                    style={active ? {
                      background: 'hsl(var(--amber-bg))',
                      color: 'hsl(var(--amber))',
                    } : {}}
                  >
                    {active && (
                      <div className="absolute left-0 top-[20%] bottom-[20%] w-[3px] rounded-r bg-primary" />
                    )}
                    <Icon size={14} className={`flex-shrink-0 ${active ? 'opacity-100' : 'opacity-100'}`} />
                    {item.label}
                    {badgeCount > 0 && (
                      <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${getBadgeStyle((item as any).badge)}`}>
                        {badgeCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
            )
          })}
        </nav>

        {/* Sync status */}
        <div className="flex items-center gap-1.5 px-2.5 pb-1.5 text-[10px] font-semibold text-muted-foreground" style={{ opacity: 1 }}>
          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'hsl(var(--emerald))' }} />
          Sincronizado
        </div>

        {/* Construzilla branding footer */}
        <div className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2" style={{ borderTop: '1px solid hsl(var(--line))', background: 'transparent' }}>
          <img
            src="/construzilla-logo.png?v=6"
            alt="Construzilla"
            style={{ height: 32, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }}
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
          <span className="text-[11px] font-bold" style={{ color: 'hsl(var(--amber))' }}>Construzilla</span>
        </div>

        {/* User footer */}
        <div className="flex-shrink-0 px-1.5 pb-1.5" style={{ borderTop: '1px solid hsl(var(--line))' }}>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors hover:bg-secondary"
          >
            <div className="w-[30px] h-[30px] rounded-full bg-primary flex items-center justify-center text-[11px] font-bold text-primary-foreground flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 text-left min-w-0">
              <div className="text-xs font-semibold text-foreground truncate">
                {(user?.nome || '').split(' ').slice(0, 2).join(' ')}
              </div>
              <div className="text-[10px] font-semibold text-muted-foreground mt-0.5 flex items-center gap-1" style={{ opacity: 1 }}>
                <RoleIcon size={9} className="flex-shrink-0" />
                <span className="truncate">{roleLabel}</span>
                {company?.plan && (
                  <>
                    <span>·</span>
                    <span className="uppercase text-[8px] font-bold" style={{ color: 'hsl(var(--amber))' }}>{company.plan}</span>
                  </>
                )}
              </div>
            </div>
            <LogOut size={12} className="text-muted-foreground flex-shrink-0" />
          </button>
        </div>
      </aside>
    </>
  )
}
